import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect } from 'react';
import authedFetch from '../lib/authedFetch';
import {
  ChevronRight, ChevronLeft, Check, Calculator, Target, Calendar,
  Sparkles, AlertCircle, CheckCircle, Loader2, Info, X, Users, GraduationCap
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pillar colors
const PILLAR_COLORS = {
  P1: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700' },
  P2: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
  P3: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700' },
  P4: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' },
  P5: { bg: 'bg-rose-50', border: 'border-rose-200', badge: 'bg-rose-100 text-rose-700' }
};

// Step indicator component
const StepIndicator = ({ steps, currentStep }) => (
  <div className="flex items-center justify-center mb-8">
    {steps.map((step, index) => (
      <React.Fragment key={step.id}>
        <div className="flex flex-col items-center">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
              index < currentStep
                ? 'bg-emerald-500 text-white'
                : index === currentStep
                ? 'bg-indigo-600 text-white ring-4 ring-indigo-100'
                : 'bg-gray-200 text-gray-500'
            }`}
          >
            {index < currentStep ? <Check size={20} /> : index + 1}
          </div>
          <span className={`text-xs mt-1 font-medium ${
            index === currentStep ? 'text-indigo-600' : 'text-gray-500'
          }`}>
            {step.label}
          </span>
        </div>
        {index < steps.length - 1 && (
          <div
            className={`w-16 h-1 mx-2 rounded ${
              index < currentStep ? 'bg-emerald-500' : 'bg-gray-200'
            }`}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Step 1: Select Measure
const StepSelectMeasure = ({ formulas, selectedMeasure, onSelect, selectedPillar, onPillarChange }) => {
  const pillars = [...new Set(formulas.map(f => f.pillar))].sort();
  const filteredFormulas = selectedPillar === 'all'
    ? formulas
    : formulas.filter(f => f.pillar === selectedPillar);

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Select a Measure</h3>
        <p className="text-gray-500 mt-1">Choose which LEI measure you want to import data for</p>
      </div>

      {/* Pillar Filter */}
      <div className="flex items-center gap-4 justify-center">
        <Label className="text-sm text-gray-600">Filter by Pillar:</Label>
        <Select value={selectedPillar} onValueChange={onPillarChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Pillars" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Pillars</SelectItem>
            {pillars.map(pillar => (
              <SelectItem key={pillar} value={pillar}>{pillar}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Measure Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-2">
        {filteredFormulas.map((formula) => {
          const pillarStyle = PILLAR_COLORS[formula.pillar] || PILLAR_COLORS.P1;
          const isSelected = selectedMeasure?.id === formula.id;

          return (
            <button
              key={formula.id}
              onClick={() => onSelect(formula)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                isSelected
                  ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                  : `${pillarStyle.border} ${pillarStyle.bg} hover:border-indigo-300`
              }`}
              data-testid={`measure-option-${formula.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={pillarStyle.badge}>{formula.pillar}</Badge>
                    {isSelected && <Check size={16} className="text-indigo-600" />}
                  </div>
                  <h4 className="font-semibold text-gray-900">{formula.name}</h4>
                  <p className="text-xs text-gray-500 mt-1">{formula.component_count} components</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Step 2: Enter Component Values
const StepEnterValues = ({ measure, componentValues, onValueChange }) => {
  if (!measure) return null;

  const pillarStyle = PILLAR_COLORS[measure.pillar] || PILLAR_COLORS.P1;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Enter Component Values</h3>
        <p className="text-gray-500 mt-1">Fill in the values for each component (0-100 scale)</p>
      </div>

      {/* Selected Measure Info */}
      <div className={`p-4 rounded-lg ${pillarStyle.bg} ${pillarStyle.border} border`}>
        <div className="flex items-center gap-2 mb-2">
          <Badge className={pillarStyle.badge}>{measure.pillar}</Badge>
          <span className="font-semibold text-gray-900">{measure.name}</span>
        </div>
        <p className="text-sm text-gray-600 font-mono">{measure.formula}</p>
      </div>

      {/* Component Inputs */}
      <div className="space-y-4">
        {measure.components?.map((comp) => (
          <div
            key={comp.id}
            className="p-4 bg-white rounded-lg border border-gray-200 space-y-2"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <Label htmlFor={comp.id} className="font-medium text-gray-900">
                  {comp.name}
                </Label>
                <p className="text-xs text-gray-500">{comp.description}</p>
                <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                  <span>Weight: {(comp.weight * 100).toFixed(0)}%</span>
                  {comp.benchmark_low && (
                    <span>Benchmark: {comp.benchmark_low}-{comp.benchmark_high}</span>
                  )}
                </div>
              </div>
              <div className="w-32">
                <Input
                  id={comp.id}
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="0-100"
                  value={componentValues[comp.id] || ''}
                  onChange={(e) => onValueChange(comp.id, e.target.value)}
                  className="text-right font-mono"
                  data-testid={`component-input-${comp.id}`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tip */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800 text-sm">
          <strong>Tip:</strong> You don't need to fill in all components. 
          The system will calculate a score based on available data.
        </AlertDescription>
      </Alert>
    </div>
  );
};

// Step 3: Set Period
const StepSetPeriod = ({ period, onPeriodChange }) => {
  const currentYear = new Date().getFullYear();
  const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3);

  const quarters = [
    { value: `${currentYear}-Q1`, label: `${currentYear} Q1 (Jan-Mar)` },
    { value: `${currentYear}-Q2`, label: `${currentYear} Q2 (Apr-Jun)` },
    { value: `${currentYear}-Q3`, label: `${currentYear} Q3 (Jul-Sep)` },
    { value: `${currentYear}-Q4`, label: `${currentYear} Q4 (Oct-Dec)` },
    { value: `${currentYear - 1}-Q4`, label: `${currentYear - 1} Q4` },
    { value: `${currentYear - 1}-Q3`, label: `${currentYear - 1} Q3` },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Set Measurement Period</h3>
        <p className="text-gray-500 mt-1">When was this data collected?</p>
      </div>

      <div className="max-w-md mx-auto space-y-4">
        <div>
          <Label className="text-sm text-gray-600 mb-2 block">Select Period</Label>
          <Select value={period} onValueChange={onPeriodChange}>
            <SelectTrigger className="w-full" data-testid="period-select">
              <SelectValue placeholder="Select a period" />
            </SelectTrigger>
            <SelectContent>
              {quarters.map(q => (
                <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="text-center text-sm text-gray-500">or</div>

        <div>
          <Label className="text-sm text-gray-600 mb-2 block">Custom Period</Label>
          <Input
            type="text"
            placeholder="e.g., 2025-Q1, 2025-H1, Jan 2025"
            value={period}
            onChange={(e) => onPeriodChange(e.target.value)}
            className="text-center"
            data-testid="custom-period-input"
          />
        </div>
      </div>

      <Alert className="bg-amber-50 border-amber-200 max-w-md mx-auto">
        <Calendar className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800 text-sm">
          <strong>Suggested:</strong> Use quarterly periods (e.g., 2025-Q1) for consistent tracking.
        </AlertDescription>
      </Alert>
    </div>
  );
};

// Step 4: Review & Submit
const StepReview = ({ measure, componentValues, period, calculationPreview, isCalculating, cohorts = [], programs = [], selectedCohortIds = [], selectedProgramIds = [] }) => {
  const pillarStyle = PILLAR_COLORS[measure?.pillar] || PILLAR_COLORS.P1;
  const filledComponents = Object.entries(componentValues).filter(([_, v]) => v !== '' && v !== null);

  const selectedCohorts = cohorts.filter(c => selectedCohortIds.includes(c.id));
  const selectedPrograms = programs.filter(p => selectedProgramIds.includes(p.id));

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Review & Import</h3>
        <p className="text-gray-500 mt-1">Verify your data before importing</p>
      </div>

      {/* Summary Card */}
      <Card className={`${pillarStyle.bg} ${pillarStyle.border}`}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Badge className={pillarStyle.badge}>{measure?.pillar}</Badge>
            {measure?.name}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Period */}
          <div className="flex items-center justify-between py-2 border-b border-gray-200">
            <span className="text-gray-600">Period</span>
            <Badge variant="outline">{period || 'Not set'}</Badge>
          </div>

          {/* Segments */}
          {(selectedCohorts.length > 0 || selectedPrograms.length > 0) && (
            <div className="py-2 border-b border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Segments</p>
              <div className="flex flex-wrap gap-2">
                {selectedCohorts.map(c => (
                  <Badge key={c.id} className="bg-blue-100 text-blue-700">
                    <Users size={12} className="mr-1" />
                    {c.name}
                  </Badge>
                ))}
                {selectedPrograms.map(p => (
                  <Badge key={p.id} className="bg-purple-100 text-purple-700">
                    <GraduationCap size={12} className="mr-1" />
                    {p.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Component Values */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Component Values ({filledComponents.length}/{measure?.components?.length})
            </p>
            <div className="space-y-2">
              {measure?.components?.map((comp) => {
                const value = componentValues[comp.id];
                const hasValue = value !== '' && value !== null && value !== undefined;
                return (
                  <div
                    key={comp.id}
                    className={`flex items-center justify-between p-2 rounded ${
                      hasValue ? 'bg-white' : 'bg-gray-100'
                    }`}
                  >
                    <span className={`text-sm ${hasValue ? 'text-gray-900' : 'text-gray-400'}`}>
                      {comp.name}
                    </span>
                    <span className={`font-mono font-medium ${
                      hasValue ? 'text-indigo-600' : 'text-gray-400'
                    }`}>
                      {hasValue ? parseFloat(value).toFixed(1) : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Calculation Preview */}
          {calculationPreview && !isCalculating && (
            <div className="pt-4 border-t border-gray-200">
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500 mb-1">Calculated Score</p>
                <p className="text-4xl font-bold text-indigo-600">
                  {calculationPreview.calculated_score?.toFixed(1)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {calculationPreview.data_completeness?.toFixed(0)}% data completeness
                </p>
              </div>
            </div>
          )}

          {isCalculating && (
            <div className="pt-4 border-t border-gray-200">
              <div className="bg-white rounded-lg p-4 text-center">
                <Loader2 className="animate-spin mx-auto text-indigo-600" size={32} />
                <p className="text-sm text-gray-500 mt-2">Calculating...</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Step 5: Success
const StepSuccess = ({ result, onClose, onImportAnother }) => (
  <div className="text-center space-y-6 py-8">
    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
      <CheckCircle className="text-emerald-600" size={48} />
    </div>
    
    <div>
      <h3 className="text-2xl font-bold text-gray-900">Import Successful!</h3>
      <p className="text-gray-500 mt-2">Your data has been saved and the score has been calculated.</p>
    </div>

    {result && (
      <Card className="max-w-md mx-auto bg-gradient-to-br from-indigo-50 to-white">
        <CardContent className="pt-6">
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-1">Calculated Score</p>
            <p className="text-5xl font-bold text-indigo-600">
              {result.calculation_result?.calculated_score?.toFixed(1)}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {result.calculation_result?.data_completeness?.toFixed(0)}% data completeness
            </p>
          </div>
        </CardContent>
      </Card>
    )}

    <div className="flex items-center justify-center gap-4 pt-4">
      <Button variant="outline" onClick={onClose} data-testid="close-wizard-btn">
        Close
      </Button>
      <Button onClick={onImportAnother} className="bg-indigo-600 hover:bg-indigo-700" data-testid="import-another-btn">
        <Sparkles size={16} className="mr-2" />
        Import Another
      </Button>
    </div>
  </div>
);

// Main Wizard Component
export default function QuickImportWizard({ isOpen, onClose, formulas = [], onImportComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedMeasure, setSelectedMeasure] = useState(null);
  const [selectedPillar, setSelectedPillar] = useState('all');
  const [componentValues, setComponentValues] = useState({});
  const [period, setPeriod] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationPreview, setCalculationPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  
  // Segment state
  const [cohorts, setCohorts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedCohortIds, setSelectedCohortIds] = useState([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);

  const steps = [
    { id: 'measure', label: 'Measure' },
    { id: 'values', label: 'Values' },
    { id: 'period', label: 'Period' },
    { id: 'segments', label: 'Segments' },
    { id: 'review', label: 'Review' },
    { id: 'success', label: 'Done' },
  ];

  // Fetch cohorts and programs
  useEffect(() => {
    if (isOpen) {
      fetchSegments();
    }
  }, [isOpen]);

  const fetchSegments = async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const [cohortsRes, programsRes] = await Promise.all([
        authedFetch(`${API_URL}/api/segments/cohorts`, { headers: { 'Authorization': `Bearer ${token}` } }),
        authedFetch(`${API_URL}/api/segments/programs`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (cohortsRes.ok) {
        const data = await cohortsRes.json();
        setCohorts(data.cohorts || []);
      }
      if (programsRes.ok) {
        const data = await programsRes.json();
        setPrograms(data.programs || []);
      }
    } catch (error) {
      logger.error('Failed to fetch segments:', error);
    }
  };

  // Reset when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setSelectedMeasure(null);
      setComponentValues({});
      setPeriod('');
      setSelectedCohortIds([]);
      setSelectedProgramIds([]);
      setCalculationPreview(null);
      setImportResult(null);
    }
  }, [isOpen]);

  // Calculate preview when entering review step
  useEffect(() => {
    if (currentStep === 4 && selectedMeasure && Object.keys(componentValues).length > 0) {
      calculatePreview();
    }
  }, [currentStep]);

  const calculatePreview = async () => {
    setIsCalculating(true);
    try {
      const token = secureStorage.getItem('session_token');
      const rawData = {};
      Object.entries(componentValues).forEach(([key, value]) => {
        if (value !== '' && value !== null) {
          rawData[key] = parseFloat(value);
        }
      });

      const response = await authedFetch(`${API_URL}/api/lei-components/calculate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          measure_id: selectedMeasure.id,
          raw_data: rawData
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCalculationPreview(data);
      }
    } catch (error) {
      logger.error('Preview calculation error:', error);
    }
    setIsCalculating(false);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const token = secureStorage.getItem('session_token');
      const rawData = {};
      Object.entries(componentValues).forEach(([key, value]) => {
        if (value !== '' && value !== null) {
          rawData[key] = parseFloat(value);
        }
      });

      const response = await authedFetch(`${API_URL}/api/lei-components/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          measure_id: selectedMeasure.id,
          raw_data: rawData,
          period: period,
          cohort_ids: selectedCohortIds,
          program_ids: selectedProgramIds,
          source: 'quick_import'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setImportResult(data);
        setCurrentStep(5); // Move to success step
        toast.success('Data imported successfully!');
        if (onImportComplete) {
          onImportComplete(data);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Import failed');
      }
    } catch (error) {
      logger.error('Import error:', error);
      toast.error('Failed to import data');
    }
    setIsSubmitting(false);
  };

  const handleValueChange = (componentId, value) => {
    setComponentValues(prev => ({
      ...prev,
      [componentId]: value
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return selectedMeasure !== null;
      case 1:
        return Object.values(componentValues).some(v => v !== '' && v !== null);
      case 2:
        return period.trim() !== '';
      case 3:
        return true; // Segments are optional
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep === 4) {
      handleSubmit();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleImportAnother = () => {
    setCurrentStep(0);
    setSelectedMeasure(null);
    setComponentValues({});
    setPeriod('');
    setSelectedCohortIds([]);
    setSelectedProgramIds([]);
    setCalculationPreview(null);
    setImportResult(null);
  };

  const toggleCohort = (cohortId) => {
    setSelectedCohortIds(prev =>
      prev.includes(cohortId)
        ? prev.filter(id => id !== cohortId)
        : [...prev, cohortId]
    );
  };

  const toggleProgram = (programId) => {
    setSelectedProgramIds(prev =>
      prev.includes(programId)
        ? prev.filter(id => id !== programId)
        : [...prev, programId]
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quick-import-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="text-indigo-600" size={24} />
            Quick Import Wizard
          </DialogTitle>
          <DialogDescription>
            Import component data step-by-step without creating a CSV file
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        {currentStep < 5 && <StepIndicator steps={steps} currentStep={currentStep} />}

        {/* Step Content */}
        <div className="min-h-[400px]">
          {currentStep === 0 && (
            <StepSelectMeasure
              formulas={formulas}
              selectedMeasure={selectedMeasure}
              onSelect={setSelectedMeasure}
              selectedPillar={selectedPillar}
              onPillarChange={setSelectedPillar}
            />
          )}
          {currentStep === 1 && (
            <StepEnterValues
              measure={selectedMeasure}
              componentValues={componentValues}
              onValueChange={handleValueChange}
            />
          )}
          {currentStep === 2 && (
            <StepSetPeriod
              period={period}
              onPeriodChange={setPeriod}
            />
          )}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Assign to Segments (Optional)</h3>
                <p className="text-gray-500 mt-1">Link this data to cohorts and programs for segmented analysis</p>
              </div>

              {/* Cohorts */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Users size={16} className="text-blue-600" />
                  Cohorts
                </Label>
                {cohorts.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No cohorts available</p>
                ) : (
                  <div className="space-y-2 max-h-32 overflow-y-auto border rounded-lg p-2">
                    {cohorts.map(cohort => (
                      <label
                        key={cohort.id}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                          selectedCohortIds.includes(cohort.id)
                            ? 'bg-blue-50 border border-blue-200'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <Checkbox
                          checked={selectedCohortIds.includes(cohort.id)}
                          onCheckedChange={() => toggleCohort(cohort.id)}
                        />
                        <span className="font-medium text-sm">{cohort.name}</span>
                        <Badge variant="outline" className="text-xs">{cohort.type}</Badge>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Programs */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <GraduationCap size={16} className="text-purple-600" />
                  Programs
                </Label>
                {programs.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No programs available</p>
                ) : (
                  <div className="space-y-2 max-h-32 overflow-y-auto border rounded-lg p-2">
                    {programs.map(program => (
                      <label
                        key={program.id}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                          selectedProgramIds.includes(program.id)
                            ? 'bg-purple-50 border border-purple-200'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <Checkbox
                          checked={selectedProgramIds.includes(program.id)}
                          onCheckedChange={() => toggleProgram(program.id)}
                        />
                        <span className="font-medium text-sm">{program.name}</span>
                        <Badge variant="outline" className="text-xs">{program.status}</Badge>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  Segmentation is optional. Skip this step if you don't need to filter data by group.
                </AlertDescription>
              </Alert>
            </div>
          )}
          {currentStep === 4 && (
            <StepReview
              measure={selectedMeasure}
              componentValues={componentValues}
              period={period}
              calculationPreview={calculationPreview}
              isCalculating={isCalculating}
              cohorts={cohorts}
              programs={programs}
              selectedCohortIds={selectedCohortIds}
              selectedProgramIds={selectedProgramIds}
            />
          )}
          {currentStep === 5 && (
            <StepSuccess
              result={importResult}
              onClose={onClose}
              onImportAnother={handleImportAnother}
            />
          )}
        </div>

        {/* Navigation Buttons */}
        {currentStep < 5 && (
          <div className="flex items-center justify-between pt-6 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0}
              data-testid="wizard-back-btn"
            >
              <ChevronLeft size={16} className="mr-1" />
              Back
            </Button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                Step {currentStep + 1} of {steps.length - 1}
              </span>
            </div>

            <Button
              onClick={handleNext}
              disabled={!canProceed() || isSubmitting}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="wizard-next-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Importing...
                </>
              ) : currentStep === 4 ? (
                <>
                  <Check size={16} className="mr-1" />
                  Import Data
                </>
              ) : (
                <>
                  Next
                  <ChevronRight size={16} className="ml-1" />
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
